import { useState, useEffect } from "react";
import { BloomreachLeadModel } from "../../types/BloomreachLeadModal";
import { ContactMeContainer, ContactMeContent, ContactMeHeadline, ContactMeSubtitle, Close, CloseButton, CloseIcon, ModalInputContainer, ModalInput, ErrorMessage, Button, InputWrapper, ValidIcon, ConsentRow, ConsentCheckbox, ConsentLabel, ConsentLink } from "./BloomreachLeadModal.styles";
import Analytics from "../../utils/analytics";
import { postBloomreachLead } from "../../utils/api";
import { getCookie } from "../../utils/cookieService";

export default function BloomreacLeadModal() {
  const popup = document.querySelector(`[data-component='bloomreach-lead-modal']`) as HTMLElement;

  const [showEmailInput, setShowEmailInput] = useState<boolean>(false);
  const [showMobileInput, setShowMobileInput] = useState<boolean>(false);

  const [showContactMe, setShowContactMe] = useState<boolean>(false);
  const [firstName, setFirstName] = useState<string>();
  const [mobile, setMobile] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [showMobileError, setShowMobileError] = useState<boolean>(false);
  const [showFirstNameError, setShowFirstNameError] = useState<boolean>(false);
  const [showEmailError, setShowEmailError] = useState<boolean>(false);
  const [consentChecked, setConsentChecked] = useState<boolean>(false);
  const [showConsentError, setShowConsentError] = useState<boolean>(false);

  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [leadType, setLeadType] = useState<string>("");
  const [ctaTitle, setCtaTitle] = useState<string>("");
  const [ctaUrl, setCtaUrl] = useState<string>("");

  useEffect(() => {
    if (popup) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "attributes" && mutation.attributeName === "data-show-popup") {
            const show = popup.dataset.showPopup === "true";

            setShowContactMe(show);
            if (show) {
              setConsentChecked(false);
              setShowConsentError(false);
              if (popup.dataset.showPhone === "true") {
                setShowMobileInput(true);
              }
              if (popup.dataset.showEmail === "true") {
                setShowEmailInput(true);
              }
              if (popup.dataset.popupTitle && popup.dataset.popupTitle !== "") {
                const headline = popup.dataset.popupTitle;
                setTitle(headline);
              }
              if (popup.dataset.popupDescription && popup.dataset.popupDescription !== "") {
                const description = popup.dataset.popupDescription;
                setDescription(description);
              }
              if(popup.dataset.leadType && popup.dataset.leadType !== "") {
                const leadType = popup.dataset.leadType;  
                setLeadType(leadType);
              }
              if(popup.dataset.popupCtaTitle && popup.dataset.popupCtaTitle !== "") {
                const ctaTitle = popup.dataset.popupCtaTitle;  
                setCtaTitle(ctaTitle);
              }
              if(popup.dataset.ctaUrl && popup.dataset.ctaUrl !== "") {
                const ctaUrl = popup.dataset.ctaUrl;  
                setCtaUrl(ctaUrl);
              }
            }
          }
        });
      });
      observer.observe(popup, { attributes: true });
      return () => {
        observer.disconnect();
      };
    }
  }, [popup]);

  //Api send contact me
  const onSend = async () => {
    let lead = {} as BloomreachLeadModel;
    lead.firstName = firstName as string;
    if(showMobileInput) {
      lead.phone = mobile as string;
    }
    if(showEmailInput) {
    lead.email = email as string;
    }
    lead.campaign = leadType === "soft" ? "softlead" : "Ring mig op";
    lead.source = `leadpopup_${window.location.pathname}`;
    lead.occupation = "";
    lead.comment = "";

    const cookie = getCookie("__exponea_etc__");
      if (cookie) {
        lead.cookie = cookie;
      }

    await postBloomreachLead({
        ...lead
        }).then(() => {
        setShowContactMe(false);
        if(leadType === "soft") {
          Analytics.getInstance().trackLeadFromPopupComponent(`softlead+${window.location.pathname}`);
        } else {
          Analytics.getInstance().trackLeadFromPopupComponent(`ring_mig_op+${window.location.pathname}`);
        }
        if(ctaUrl) {   
          window.location.href = ctaUrl;
        }
      }).catch((error) => {
        console.error("Error sending lead:", error);
      });
  };

  const validateFirstName = (value?: string) => !!value?.trim();
  const validateMobile = (value?: string) => {
    const normalized = value?.trim().replace(/[\s-]/g, "");
    if (!normalized) return false;

    // Accept: 8 digits (DK local), +45 + 8 digits, 0045 + 8 digits
    if (normalized.startsWith("+45")) {
      return /^\+45\d{8}$/.test(normalized);
    }
    if (normalized.startsWith("0045")) {
      return /^0045\d{8}$/.test(normalized);
    }

    return /^\d{8}$/.test(normalized);
  };
  const validateEmail = (value?: string) => {
    const normalized = value?.trim();
    return !!normalized && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
  };

  //Validation for contact me inputs
  const checkSubmit = () => {
    let isValid = true;

    const normalizedFirstName = firstName?.trim();
    const normalizedMobile = mobile?.trim();
    const normalizedEmail = email?.trim();

    // Name is always required
    if (!normalizedFirstName) {
      setShowFirstNameError(true);
      isValid = false;
    } else {
      setShowFirstNameError(false);
    }

    // Phone is required only when phone input is shown
    if (showMobileInput) {
      if (!normalizedMobile) {
        setShowMobileError(true);
        isValid = false;
      } else {
        setShowMobileError(false);
      }
    } else {
      setShowMobileError(false);
    }

    // Email is required only when email input is shown
    if (showEmailInput) {
      const isEmailValid = validateEmail(normalizedEmail);
      if (!isEmailValid) {
        setShowEmailError(true);
        isValid = false;
      } else {
        setShowEmailError(false);
      }
    } else {
      setShowEmailError(false);
    }

    if (!consentChecked) {
      setShowConsentError(true);
      isValid = false;
    } else {
      setShowConsentError(false);
    }

    if (!isValid) {
      return;
    }

    onSend();

    // reset inputs after successful submission
    setFirstName("");
    setMobile("");
    setEmail("");
    setConsentChecked(false);
    setShowConsentError(false);
  };

  const isFirstNameValid = validateFirstName(firstName);
  const isMobileValid = !showMobileInput || validateMobile(mobile);
  const isEmailValid = !showEmailInput || validateEmail(email);
  const isFormValid = isFirstNameValid && isMobileValid && isEmailValid && consentChecked;


  return showContactMe ? (
    <ContactMeContainer data-attribute="popup-lead-modal">
      <Close>
        <CloseIcon onClick={() => setShowContactMe(false)} />
      </Close>
      <ContactMeContent>
        <ContactMeHeadline>{title}</ContactMeHeadline>
        <ContactMeSubtitle>{description}</ContactMeSubtitle>
        <ModalInputContainer>
          <InputWrapper>
          <ModalInput
            id="name"
            type="text"
            placeholder="Dit navn"
            autoComplete="given-name"
            isValid={isFirstNameValid}
            isInvalid={showFirstNameError && !isFirstNameValid}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setFirstName(value);
              setShowFirstNameError(!validateFirstName(value));
            }}
          />
          {isFirstNameValid && <ValidIcon />}
        </InputWrapper>
        {showFirstNameError && <ErrorMessage>Hov, her skal dit navn stå</ErrorMessage>}
          {showMobileInput && (
            <InputWrapper>
              <ModalInput
                id="tel"
                type="tel"
                placeholder="Dit telefonnummer"
                autoComplete="tel"
                isValid={isMobileValid}
                isInvalid={showMobileError && !isMobileValid}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  setMobile(value);
                  setShowMobileError(!validateMobile(value));
                }}
              />
              {isMobileValid && <ValidIcon />}
            </InputWrapper>
          )}
          {showMobileInput && showMobileError && <ErrorMessage>Hov, vi kan ikke ringe til dig uden telefonnummer</ErrorMessage>}
          {showEmailInput && (
            <InputWrapper>
              <ModalInput
                id="email"
                type="email"
                placeholder="Din email"
                autoComplete="email"
                isValid={isEmailValid}
                isInvalid={showEmailError && !isEmailValid}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  setEmail(value);
                  setShowEmailError(!validateEmail(value));
                }}
              />
              {isEmailValid && <ValidIcon />}
            </InputWrapper>
          )}
          {showEmailInput && showEmailError && <ErrorMessage>Hov, vi kan ikke skrive til dig uden email</ErrorMessage>}
          <ConsentRow>
            <ConsentCheckbox
              id="acceptContact"
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => {
                const isChecked = e.currentTarget.checked;
                setConsentChecked(isChecked);
                if (showConsentError) {
                  setShowConsentError(!isChecked);
                }
              }}
            />
            <ConsentLabel htmlFor="acceptContact">
              Ja tak - jeg vil gerne modtage eksklusive rabatter og medlemstilbud fra Ases <ConsentLink target="_blank" rel="noopener noreferrer" href="https://www.ase.dk/gdpr/ase-a-kasse-samarbejdspartnere">fordelspartnere</ConsentLink>) samt udvalgte medlemsfordele fra Ase, som kan spare mig penge i hverdagen. <br />Det er altid Ase, der kontakter mig.<br />Jeg ma kontaktes via e-mail, sms og telefon. Mit samtykke kan altid traekkes tilbage.
            </ConsentLabel>
          </ConsentRow>
          {showConsentError && <ErrorMessage>Hov, du skal give samtykke for at fortsatte</ErrorMessage>}
        </ModalInputContainer>
        <Button
          className="button"
          onClick={() => isFormValid && checkSubmit()}
          active={isFormValid}
          disabled={!isFormValid}
          aria-disabled={!isFormValid}
        >
          {ctaTitle ? ctaTitle : "Ring mig op"}
        </Button>
        <a href="https://www.ase.dk/cookie-privatliv-og-persondata" target="_blank" rel="noopener noreferrer" style={{ marginTop: "20px", fontSize: "12px", color: "#FFFFFF" }}>
          Sådan behandler vi dine oplysninger
        </a>
      </ContactMeContent>
    </ContactMeContainer>
  ) : null;
}
